<template>
  <div class="select-input" :class="{ 'has-error': error }">
    <select
      ref="actualInput"
      class="select-native"
      v-model="internalValue"
      :disabled="disabled"
      :readonly="readonly"
      :aria-invalid="error ? 'true' : 'false'"
      @change="onChange(internalValue)"
      :style="{ minWidth: minWidth ? minWidth + 'px' : 'auto' }"
    >
      <option value="" disabled></option>
      <option
        v-for="(item, index) in normalizedItems"
        :key="index"
        :value="item"
      >
        {{ item }}
      </option>
    </select>
    <div :class="['v-input__details', { 'invisible': !error }]" role="alert" aria-live="polite">
      <div class="v-messages">
        <div class="v-messages__message" v-for="(msg, i) in errorMessages" :key="i">{{ msg }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  modelValue: [String, Number],
  items: {
    type: Array,
    required: true,
  },
  itemValue: {
    type: Function,
    default: (item) => (typeof item === 'object' ? item.value : item),
  },
  itemText: {
    type: Function,
    default: (item) => (typeof item === 'object' ? item.text : item),
  },
  disabled: Boolean,
  readonly: Boolean,
  rules: {
    type: Array,
    default: () => [],
  },
  minWidth: Number,
})

const emit = defineEmits(['submit', 'formIsInvalid'])

const internalValue = ref(null)
const errorMessages = ref([])
const error = ref(false)
const actualInput = ref(null)

const normalizedItems = computed(() => props.items || [])


function onChange(val) {
  if (validate(val)) {
    emit('submit', val)
  }
}

function validate(val = null) {
  if (val === null) {
    val = internalValue.value
  }
  errorMessages.value = []

  for (const rule of props.rules || []) {
    let result
    try {
      result = rule(val)
    } catch (e) {
      result = e.message || 'Validation error'
    }

    if (result !== true) {
      error.value = true
      errorMessages.value.push(result)
      emit('formIsInvalid')
      return false
    }
  }

  error.value = false
  return true
}

defineExpose({
  validate,
  focus() {
    actualInput.value.focus()
  },
  scrollIntoView(e) {
    actualInput.value.scrollIntoView(e)
  },
  resetValidation() {
    errorMessages.value = []
    error.value = false
  }
})

watch(() => props.modelValue, (val) => {
  internalValue.value = val
}, { immediate: true })
</script>

<style scoped>
.select-input {
  display: flex;
  flex-direction: column;
  font-size: 14px;
}

select,
::picker(select) {
  appearance: base-select;
}

select.select-native {
  padding: 6px;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 4px;
  flex: 1;
  transition: 0.4s;
}

select::picker-icon {
  color: #999;
  transition: 0.4s rotate;
}
select:open::picker-icon {
  rotate: 180deg;
}

select.select-native:focus {
  outline: none;
  border-color: rgba(0,0,0,0.12);
}
.has-error select.select-native {
  border-color: rgba(0,0,0,0.12);
}
</style>
